import React, { Suspense, useState, useEffect, Fragment, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import qs from 'qs';
import useDataLayer from 'src/hooks/useDataLayer';
import usePrevious from 'src/hooks/usePrevious';
import useBikeTotal from 'src/hooks/useBikeTotal';
import useSteps from 'src/hooks/useSteps';
import fetchGoal from 'helpers/goals';
import request from 'helpers/request';
import {
  getTwoLetterIsoName,
  getTwoLetterIsoLanguageName,
  updateLinkSubmit,
  removeDuplicateParams,
} from 'helpers/utilities';
import {
  currentStepSelector,
  openModalSelector,
  getBikeZoomed,
  getPacks,
  accessoryItemsSelector,
  modalPropsSelector,
} from 'src/redux/configurator/selectors';
import { getProvinces } from 'src/redux/app/selectors';
import {
  bikeModelSelector,
  getPendingAccessories,
  getAccessoryPendingRemoval,
  bikeColourSelector,
  getPackPendingRemoval,
  getPendingPackConflicts,
  getBikeAccessories,
  getPackAccessories,
  getSelectedServicePack,
  getSelectedWarrantyPack,
} from 'src/redux/bike/selectors';
import {
  openModal,
  closeModal,
  setAccessoriesCategory,
  setSelectedDisclaimer,
  setAccessoryItems,
  setAccessoryCategories,
  setAccessoryExtras,
  closeAllModals,
  setPacks,
  setScrollYPosition,
  setScrollXPosition,
  resetConfigurator,
  setModalProps,
  setSelectedProvince,
} from 'src/redux/configurator/actions';
import { setModel, loadConfiguration, resetBike } from 'src/redux/bike/actions';
import { setError } from 'src/redux/app/actions';
import Stepper from 'components/Stepper/Stepper';
import BikeImage from 'components/BikeImage/BikeImage';
import ShareModal from 'components/ShareModal/ShareModal';
import AccessoryInfoModal from 'components/_modals/AccessoryInfoModal/AccessoryInfoModal';
import Modal from 'components/_utility/Modal/Modal';
import SaveQuoteModal from 'components/_modals/SaveQuoteModal/SaveQuoteModal';
import Tray from 'components/Tray/Tray';
import Loader from 'components/_utility/Loader/Loader';
import DisclaimerModal from 'components/DisclaimerModal/DisclaimerModal';
import PendingAccessoriesSwitch from 'components/PendingAccessoriesSwitch/PendingAccessoriesSwitch';
import EmailModal from 'components/EmailModal/EmailModal';
import { useNavigate } from 'helpers/routing';
import useSaveToGarage from 'src/hooks/useSaveToGarage';
import { setLoadedConfiguration, clearLoadedConfiguration, setSaving } from 'src/redux/garage/actions';
import UnsavedConfigurationModal from 'components/UnsavedConfigurationModal/UnsavedConfigurationModal';
import { resetTagging } from 'src/redux/tagging/actions';
import useChooseColour from 'src/hooks/useChooseColour';
import Pane from 'components/_utility/Pane/Pane';
import SendToDealer from 'components/_modals/SendToDealer/SendToDealer';
import BikeSummary from 'components/_steps/BikeSummary/BikeSummary';
import { getLoadedConfiguration, getSaving } from 'src/redux/garage/selectors';
import { ctaSelector, getLabels, getGoalId } from 'src/redux/app/selectors';
import useLabel from 'src/hooks/useLabel';
import { getInteractions } from 'src/redux/tagging/selectors';
import { Overlay } from 'components/_utility/Modal/Modal.styled';
import { getGarageEmailAddress } from 'helpers/garage';
import useLeaveConfigurator from 'src/hooks/useLeaveConfigurator';
import { Wrap, BikeViewport, StepPane } from './Configurator.styled';

const API_ORIGIN = window.location.origin.includes('localhost')
  ? 'https://www.triumph-test.info'
  : window.location.origin;

const appendFinanceCalcScript = (url, setScriptLoading, provider) => {
  if (document.querySelector('[data-calc="true"]')) {
    setScriptLoading(false);
  }

  if (url === null || url === '') {
    return;
  }

  setScriptLoading(true);

  if (provider === 'santander') {
    const div = document.createElement('div');
    div.id = 'santander-modal-hidden';
    div.hidden = true;
    div.appendChild(document.createElement('scf-quoting'));
    document.body.appendChild(div);
  }

  const scriptEl = document.createElement('script');
  scriptEl.src = url;
  scriptEl.dataset.calc = true;
  document.body.appendChild(scriptEl);

  scriptEl.addEventListener('load', () => setScriptLoading(false));

  return scriptEl;
};

const removeFinanceCalcScript = isSantander => {
  // Script shouldn't be removed for Santander at the moment.
  // See TRIUNSC-9 for more info.
  if (isSantander) {
    return;
  }

  const oldScript = document.querySelector('[data-calc="true"]');
  if (oldScript) {
    document.body.removeChild(oldScript);
  }

  // Is Santander
  if (isSantander) {
    const scfWrap = document.querySelector('scf-quoting');

    if (scfWrap) {
      document.body.removeChild(scfWrap);
    }

    if (window.scf?.timer) {
      clearTimeout(window.scf.timer);
    }

    window.scfSimulation = undefined;
    window.scfPlugin = undefined;
    window.ScfPlugin = undefined;
    window.scf = undefined;
  } else {
    window.codeweavers = undefined;
  }
};

const Configurator = ({ bikeId, location }) => {
  const dispatch = useDispatch();
  const pushToDataLayer = useDataLayer();
  const steps = useSteps();
  const saveToGarage = useSaveToGarage();
  const stepPaneRef = useRef();
  const bikeViewportRef = useRef();
  const summaryHeadingRef = useRef();
  const total = useBikeTotal();
  const chooseColour = useChooseColour();
  const getLabel = useLabel();
  const leaveConfigurator = useLeaveConfigurator();

  const [saveQuoteModalOpen, setSaveQuoteModalOpen] = useState(false);
  const [accessoriesLoading, setAccessoriesLoading] = useState(true);
  const prevAccessoriesLoading = usePrevious(accessoriesLoading);
  const [configurationLoading, setConfigurationLoading] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(80);
  const [scriptLoading, setScriptLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [isSimulationChecksumExists, setIsSimulationChecksumExists] = useState(false);

  const accessories = useSelector(accessoryItemsSelector);
  const packs = useSelector(getPacks);
  const bikeModel = useSelector(bikeModelSelector);
  const bikeColour = useSelector(bikeColourSelector);
  const selectedAccessories = useSelector(getBikeAccessories);
  const selectedPackAccessories = useSelector(getPackAccessories);
  const selectedServicePack = useSelector(getSelectedServicePack);
  const selectedWarrantyPack = useSelector(getSelectedWarrantyPack);
  const openModals = useSelector(openModalSelector);
  const activeStepNumber = useSelector(currentStepSelector);
  const pendingAccessories = useSelector(getPendingAccessories);
  const pendingPackConflicts = useSelector(getPendingPackConflicts);
  const accessoryPendingRemoval = useSelector(getAccessoryPendingRemoval);
  const isBikeZoomed = useSelector(getBikeZoomed);
  const packPendingRemoval = useSelector(getPackPendingRemoval);
  const saveModalProps = useSelector(modalPropsSelector('saveToGarage'));
  const loadedConfiguration = useSelector(getLoadedConfiguration);
  const interactions = useSelector(getInteractions);
  const garageSaving = useSelector(getSaving);
  const labels = useSelector(getLabels);
  const provinces = useSelector(getProvinces);
  const ctaLinks = useSelector(ctaSelector);
  const startedConfigurationGoalId = useSelector(getGoalId('configuration started'));
  const isProcessingRef = useRef(false);
  const saveGoalId = useSelector(getGoalId('save configuration to garage'));

  const handleSaveQuote = () => {
    pushToDataLayer('configuratorCalSec', { ctaText: getLabel('saveQuoteButtonLabel') });

    setSaveQuoteModalOpen(true);
  };

  const activeStep = steps.find(step => step.number === activeStepNumber);
  const ActiveStepComponent = (activeStep && activeStep.component) || Fragment;
  const prevActiveStepNumber = usePrevious(activeStepNumber);
  const isBikeSummary = activeStepNumber === steps.length;

  const previousBikeModel = usePrevious(bikeModel);

  const navigate = useNavigate();

  const buyNowUrl = updateLinkSubmit({
    link: ctaLinks.buyNow,
    productCode: bikeModel?.productCode,
    colourCode: bikeColour?.colourCode,
    configurationID: loadedConfiguration?.id,
  });

  const { configurationId } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const secondaryCTAOverride = bikeModel?.configSummarySecondaryCTAOverride;

  let secondaryCTAUrl = '';

  if (secondaryCTAOverride) {
    secondaryCTAUrl = secondaryCTAOverride.url;
  } else if (!buyNowUrl?.includes('ModelCode')) {
    secondaryCTAUrl = `${buyNowUrl}?bikeId=${bikeModel?.id}${
      bikeColour?.id && !loadedConfiguration ? `&colourId=${bikeColour?.id}` : ``
    }`;
  } else {
    secondaryCTAUrl = buyNowUrl;
  }

  if (loadedConfiguration?.id && !secondaryCTAOverride && bikeModel?.id) {
    secondaryCTAUrl += `&configurationId=${loadedConfiguration.id}&savedConfigurationUrl=${btoa(
      `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${loadedConfiguration.id}#config`
    )}`;
  }

  const goToPageSecondary = id => {
    dispatch(setSaving(false));
    window.__configuratorRedirectTarget = true;
    const target = bikeModel?.configSummarySecondaryCTAOverride?.target || '_self';
    let redirectUrl = secondaryCTAUrl;
    const configId = id || loadedConfiguration?.id;
    if (secondaryCTAUrl.includes('start-your-order') && configId) {
      const configUrl = btoa(
        `${window.location.origin}/configure/bike/${bikeModel?.id}/configure?configurationId=${id}#config`
      );
      redirectUrl += `&configurationId=${configId}&savedConfigurationUrl=${configUrl}`;
    }
    window.open(
      removeDuplicateParams(
        updateLinkSubmit({
          link:
            redirectUrl.includes('ColourCode') && !redirectUrl.includes('configurationUrl') && configId
              ? (redirectUrl += '&ConfigurationID={configurationID}&ConfigurationUrl={configurationUrl}')
              : redirectUrl,
          productCode: bikeModel?.productCode,
          colourCode: bikeColour?.colourCode,
          configurationID: configId,
          configurationUrl: `${window.location.origin}/configure/bike/${bikeModel.id}/configure?configurationId=${configId}#config`,
        }),
        ['configurationID', 'configurationUrl', 'savedConfigurationUrl', 'configurationId']
      ),

      target
    );
  };
  const onSecondaryButton = async event => {
    event.preventDefault();
    if (getGarageEmailAddress()) {
      dispatch(setSaving(true));

      saveToGarage(null, undefined, undefined, id => goToPageSecondary(id));
    } else {
      dispatch(
        openModal('saveToGarage', {
          afterSave: id => goToPageSecondary(id),
        })
      );
    }
  };

  useEffect(() => {
    if (!ctaLinks.finance) {
      setScriptLoading(false);
    }

    const provider = ctaLinks.isFinanceProviderCodeweavers ? 'codeweavers' : 'santander';

    appendFinanceCalcScript(ctaLinks.finance, setScriptLoading, provider);

    return () => removeFinanceCalcScript(!ctaLinks.isFinanceProviderCodeweavers);
  }, []);

  // When navigating to Accessories, ensure accessory landing page is always displayed
  useEffect(() => {
    dispatch(setAccessoriesCategory(null));
  }, [activeStepNumber, dispatch]);

  const voteRemainOrLeave = event => {
    if (window.__configuratorRedirectTarget) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  };

  useEffect(() => {
    const header = document.querySelector('header');

    if (header) {
      header.style.transform = 'translateY(0%)';
    }
  }, []);

  useEffect(() => {
    fetchGoal(startedConfigurationGoalId);
  }, []);

  useEffect(() => {
    const interceptBackButton = () => window.history.pushState(null, null, '#config');
    interceptBackButton();

    const handleBackButton = () => {
      if (!window.location.href.endsWith('#')) {
        dispatch(closeAllModals());
        setTimeout(() => {
          dispatch(
            setModalProps('reset', {
              onClose: () => {
                window.addEventListener('beforeunload', voteRemainOrLeave);
                interceptBackButton();
              },
            })
          );
          dispatch(openModal('unsavedConfigurationAbandon'));
        }, 100);
      }
    };

    const anchorTags = document.querySelectorAll('a');
    const rootEl = document.querySelector('#configurator-root');
    const appAnchorTags = rootEl ? rootEl.querySelectorAll('a') : [];
    const nonAppAnchorTags = Array.from(anchorTags).filter(
      tag =>
        !Array.from(appAnchorTags).includes(tag) &&
        !tag.querySelector('.icon--search') &&
        !tag.querySelector('.action-search') &&
        !tag.classList.contains('menu-scroll-to') &&
        tag.getAttribute('href') !== '#'
    );

    const handleInternalLinkClick = event => {
      event.preventDefault();
      window.removeEventListener('beforeunload', voteRemainOrLeave);
      const link = event.target.closest('a').dataset.href;

      dispatch(closeAllModals());
      setTimeout(() => {
        dispatch(
          setModalProps('reset', {
            link,
            onReset: () => {
              nonAppAnchorTags.forEach(tag => {
                if (tag.dataset.href) {
                  tag.href = tag.dataset.href;
                  delete tag.dataset.href;
                  tag.removeEventListener('click', handleInternalLinkClick);
                }
              });
            },
            onClose: () => {
              window.addEventListener('beforeunload', voteRemainOrLeave);
              interceptBackButton();
            },
          })
        );
        dispatch(openModal('unsavedConfigurationAbandon'));
      }, 100);
    };

    nonAppAnchorTags.forEach(tag => {
      if (
        tag.target === '_blank' ||
        (tag.dataset.href && !tag.dataset.href.includes(window.location.hostname) && tag.dataset.href.includes('http'))
      ) {
        return;
      }

      tag.dataset.href = tag.href;
      tag.removeAttribute('href');
      tag.style.cursor = 'pointer';
      tag.addEventListener('click', handleInternalLinkClick);
    });

    window.addEventListener('beforeunload', voteRemainOrLeave);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('beforeunload', voteRemainOrLeave);
      window.removeEventListener('popstate', handleBackButton);

      nonAppAnchorTags.forEach(tag => {
        if (tag.dataset.href) {
          tag.href = tag.dataset.href;
          delete tag.dataset.href;
          tag.removeEventListener('click', handleInternalLinkClick);
        }
      });
    };
  }, [dispatch]);

  useEffect(() => {
    const isSearchFormSubmitted = event => {
      event.preventDefault();
      window.removeEventListener('beforeunload', voteRemainOrLeave);

      document.querySelector('html').classList.remove('search-in');

      dispatch(closeAllModals());

      setTimeout(() => {
        dispatch(
          setModalProps('reset', {
            search: true,
            onClose: () => window.addEventListener('beforeunload', voteRemainOrLeave),
          })
        );
        dispatch(openModal('unsavedConfigurationAbandon'));
      }, 100);
    };

    const searchForm = document.querySelector('.search-form');

    if (!searchForm) {
      return;
    }

    searchForm.addEventListener('submit', isSearchFormSubmitted, false);

    return () => {
      searchForm.removeEventListener('submit', isSearchFormSubmitted, false);
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchBikeData = async () => {
      try {
        const { data: bikeData } = await request(`/api/configurator/bikes/model/${bikeId}`);
        dispatch(setModel(bikeData));

        const defaultColour = bikeData.colours.find(colour => colour.isDefault);

        const { data: accessoryData } = await request(
          `/api/configurator/accessories/${bikeId}?colour=${defaultColour.id}`
        );
        dispatch(setAccessoryCategories(accessoryData.categories));
        dispatch(setAccessoryItems(accessoryData.accessories));
        dispatch(setAccessoryExtras(accessoryData.extras));

        const { data: packData } = await request(`/api/configurator/accessories/${bikeId}/packs?colour=${defaultColour.id}`);
        dispatch(setPacks(packData.packs));

        setAccessoriesLoading(false);
      } catch (ex) {
        dispatch(setError(ex));
      }
    };

    fetchBikeData();
  }, [bikeId, dispatch]);

  useEffect(() => {
    const fetchConfiguration = async () => {
      if (!accessoriesLoading && prevAccessoriesLoading !== accessoriesLoading && configurationId) {
        setConfigurationLoading(true);

        const { data: configuration } = await request(`/api/configurator/garage/${configurationId}`);

        dispatch(setLoadedConfiguration(configuration));
        setConfigurationLoading(false);

        dispatch(loadConfiguration({ accessories, packs, configuration }));
        chooseColour(configuration.colourId);

        if (
          configuration.provinceId &&
          configuration.provinceId !== '00000000-0000-0000-0000-000000000000' &&
          provinces?.length > 0
        ) {
          const savedProvince = provinces.find(province => {
            const provinceIdClean = province.id.replace(/[{}]/g, '').toLowerCase();
            const configIdClean = configuration.provinceId.replace(/[{}]/g, '').toLowerCase();
            return provinceIdClean === configIdClean;
          });
          if (savedProvince) {
            dispatch(setSelectedProvince(savedProvince));
          }
        }
      }
    };

    fetchConfiguration();
  }, [accessories, accessoriesLoading, configurationId, dispatch, packs, prevAccessoriesLoading]);

  const prevBikeColour = usePrevious(bikeColour);
  useEffect(() => {
    if (!bikeColour || accessoriesLoading || !prevBikeColour) return;
    
    if (prevBikeColour.id === bikeColour.id) return;

    const fetchPacksForColour = async () => {
      try {
        const { data: packData } = await request(
          `/api/configurator/accessories/${bikeId}/packs?colour=${bikeColour.id}`
        );
        dispatch(setPacks(packData.packs));
      } catch (ex) {
        dispatch(setError(ex));
      }
    };

    fetchPacksForColour();
  }, [bikeColour, bikeId, dispatch, accessoriesLoading, prevBikeColour]);

  useEffect(() => {
    if (bikeModel && bikeModel !== previousBikeModel) {
      pushToDataLayer('configuratorStarted');
    }
  }, [bikeModel, previousBikeModel, pushToDataLayer]);

  useEffect(() => {
    const handleSetHeaderHeight = debounce(() => {
      const headerEl = document.querySelector('header');

      if (headerEl) {
        setHeaderHeight(headerEl.offsetHeight);
      }
    }, 100);

    handleSetHeaderHeight();
    window.addEventListener('resize', handleSetHeaderHeight);

    const breadcrumb = document.querySelector('.breadcrumb');
    let removeBreadcrumb = () => {};
    if (breadcrumb) {
      breadcrumb.style.display = 'none';

      removeBreadcrumb = () => {
        breadcrumb.style.display = 'block';
      };
    }

    const mainEl = document.querySelector('main.main');
    let restoreMinHeight = () => {};
    if (mainEl) {
      mainEl.style.minHeight = 'auto';

      restoreMinHeight = () => {
        mainEl.style.minHeight = '100vh';
      };
    }

    return () => [window.removeEventListener('resize', handleSetHeaderHeight), removeBreadcrumb(), restoreMinHeight()];
  }, [bikeModel]);

  useEffect(() => {
    if (activeStepNumber !== prevActiveStepNumber) {
      dispatch(setScrollYPosition(0), setScrollXPosition(0));
    }
  }, [activeStepNumber, dispatch, prevActiveStepNumber]);

  useEffect(() => {
    if (scriptLoading || !bikeModel || !bikeColour) {
      return;
    }

    const lsQuoteRef = window.localStorage.getItem('quoteRef');
    const lsAssetId = window.localStorage.getItem('bikeId');
    const lsBikePrice = Number(window.localStorage.getItem('bikePrice'));

    const { capCode } = bikeColour;

    const assetId = capCode || `${bikeModel.year}${bikeModel.productCode}${bikeColour.colourCode}`;
    const hasExistingQuote = lsQuoteRef && assetId === lsAssetId && total === lsBikePrice;
    const isSantander = typeof window.scf === 'object';
    const isCodeweavers =
      window.codeweavers &&
      window.financeCalculator &&
      typeof window.financeCalculator.bindCodeweaversEvents === 'function';

    if (isCodeweavers) {
      setIsSimulationChecksumExists(true);
    }

    if (isCodeweavers) {
      setIsSimulationChecksumExists(true);
    }

    const getAnalyticsEnabled = () =>
      !!(window.OnetrustActiveGroups && window.OnetrustActiveGroups?.split(',').includes('C0002'));

    const hasSaveQuote = labels.saveQuoteButtonLabel?.length > 0;

    if (isSantander && window.financeCalculator) {
      const financeCalculatorParams = {
        financeFormText: getLabel('buyNowLabel'),
        financeFormSecondaryText: hasSaveQuote ? getLabel('saveQuoteButtonLabel') : undefined,
        onPrimaryClick: onSecondaryButton,
        onSecondaryClick: hasSaveQuote ? () => setSaveQuoteModalOpen(true) : undefined,
        bindLegacyQuoteModalEvents: false,
      };

      // eslint-disable-next-line no-unused-expressions
      window?.financeCalculator?.waitForScf().then(async () => {
        window.financeCalculator.setupScfModal(financeCalculatorParams);
        window.financeCalculator.updateScfModalButtons(financeCalculatorParams);
        window.financeCalculator.watchScfChanges();

        const pluginValues = {
          assetId,
          country: getTwoLetterIsoName(),
          language: getTwoLetterIsoLanguageName(),
          assetPrice: Number(total),
          assetImage: bikeColour.rightImage,
          cookie: getAnalyticsEnabled(),
          showButton: JSON.parse(true),
          model: bikeModel?.nameClean,
          simulationChecksum: hasExistingQuote ? lsQuoteRef : undefined,
        };

        // Keep for UAT debugging
        // eslint-disable-next-line
        console.log('init Santander with:', pluginValues);
        window.scfSimulation.configure(pluginValues);

        if (!window.OnetrustActiveGroups) {
          let attempts = 0;

          const waitForOnetrust = setInterval(() => {
            if (attempts >= 5) {
              return clearInterval(waitForOnetrust);
            }

            if (window.OnetrustActiveGroups) {
              window.scfSimulation.configure({
                cookie: getAnalyticsEnabled(),
              });

              return clearInterval(waitForOnetrust);
            }

            attempts += 1;
            return null;
          }, 1000);
        }

        const simulationChecksumExists = !!window.scfSimulation?.selectedSimulation?.simulationChecksum;

        window.scfSimulation.scfQuotingStatus(checkoutDetails => {
          if (checkoutDetails?.name === 'multisimulation' || checkoutDetails?.name === 'calculatorLoaded') {
            const status = checkoutDetails?.status || checkoutDetails.success;
            setIsSimulationChecksumExists(status);
          }
        });

        if (!hasExistingQuote && simulationChecksumExists) {
          window.localStorage.setItem('quoteRef', window.scfSimulation?.selectedSimulation?.simulationChecksum);
          window.localStorage.setItem('bikeId', assetId);
          window.localStorage.setItem('bikePrice', total);
        }
      });
    }

    if (isCodeweavers) {
      window.financeCalculator.bindCodeweaversEvents({
        buyNowUrl: '#',
        buyNowText: getLabel('buyNowLabel'),
        onClick: onSecondaryButton,
        saveQuoteText: getLabel('saveQuoteButtonLabel'),
        saveQuoteOnClick: handleSaveQuote,
      });
    }
  }, [
    scriptLoading,
    bikeModel,
    bikeColour,
    total,
    interactions,
    selectedAccessories,
    selectedPackAccessories,
    selectedServicePack,
    selectedWarrantyPack,
  ]);

  useEffect(() => {
    if (!loaded && isSimulationChecksumExists && window?.scfSimulation) {
      setLoaded(true);

      const visibilityCallback = ({ visible }) => {
        if (!visible) return;

        if (isProcessingRef.current) return;

        window.scfSimulation.scfEmailMarketing(async detail => {
          if (isProcessingRef.current || !detail.emailSent) return;

          isProcessingRef.current = true;

          try {
            const requestOptions = {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              mode: 'cors',
            };
            const contentRes = await request(`/api/v2/finance/quotecontent`, requestOptions);

            const postBody = {
              Email: detail.email,
              SimulationID: window?.scfSimulation?.selectedSimulation?.simulationChecksum || 'No data',
              Goal: contentRes?.data?.ViewGoalId || 'No data',
              CampaignId: contentRes?.data?.CampaignId || 'No data',
              ConsentEmail: detail.emailSent,
              BikeId: bikeModel.id,
              Link: `checkSum=${bikeModel.id}`,
            };

            const options = {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(postBody),
            };

            await fetch(`${API_ORIGIN}/api/v2/finance/quote`, options);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error sending email');
          } finally {
            isProcessingRef.current = false;
          }
        });
      };

      window.scfSimulation.scfQuotingVisibilityUpdated(visibilityCallback);

      return () => {
        // console.log('Cleanup: Disabling old listeners');
        isProcessingRef.current = false;
      };
    }
  }, [loaded, isSimulationChecksumExists, bikeModel]);

  useEffect(() => {
    window.configInteractions = interactions;
  }, [interactions]);

  const openShareModal = async () =>
    getGarageEmailAddress()
      ? [await fetchGoal(saveGoalId), await saveToGarage(), dispatch(openModal('share'))]
      : dispatch(
          openModal('saveToGarage', {
            afterSave: () => dispatch(openModal('share')),
          })
        );

  if (!bikeModel || accessoriesLoading || configurationLoading) {
    return <Loader />;
  }

  const isWide =
    pendingAccessories.length || accessoryPendingRemoval || pendingPackConflicts.length || packPendingRemoval;

  return (
    <>
      <Wrap trayHiddenOnDesktop={isBikeSummary} headerHeight={headerHeight} className="wrapper-class">
        <BikeViewport ref={bikeViewportRef} isBikeSummary={isBikeSummary}>
          <Stepper isVisible={!isWide && !isBikeSummary} isBikeSummary={isBikeSummary} isBikeZoomed={isBikeZoomed} />
          <BikeImage
            isBikeSummary={isBikeSummary && !isWide}
            canGoBack={isWide || isBikeSummary}
            className="bike-image-class"
            stepPaneRef={stepPaneRef}
            summaryHeadingRef={summaryHeadingRef}
          />
          {isBikeSummary && !isWide ? (
            <BikeSummary
              isSimulationChecksumExists={isSimulationChecksumExists}
              summaryHeadingRef={summaryHeadingRef}
              bikeViewportRef={bikeViewportRef}
              openShareModal={openShareModal}
            ></BikeSummary>
          ) : null}
        </BikeViewport>
        <StepPane
          isWide={isWide}
          isBikeZoomed={isBikeZoomed}
          isBikeSummary={isBikeSummary}
          className="step-pane-class"
          ref={stepPaneRef}
        >
          <Suspense fallback={<div />}>
            {pendingAccessories.length ||
            accessoryPendingRemoval ||
            pendingPackConflicts.length ||
            packPendingRemoval ? (
              <PendingAccessoriesSwitch />
            ) : (
              <ActiveStepComponent
                isSimulationChecksumExists={isSimulationChecksumExists}
                bikeId={bikeId}
                openShareModal={openShareModal}
                {...(activeStep.additionalProps || {})}
              />
            )}
          </Suspense>
        </StepPane>
        {openModals.loadGarage ? (
          <Modal onClose={() => dispatch(closeModal('loadGarage'))} disableFullScreen>
            <EmailModal
              showAgreeToTerms={false}
              headingLabel="loadConfigurationsLabel"
              taglineLabel="loadConfigurationsDescription"
              primaryActionLabel="loadLabel"
              primaryActionFunc={emailAddress => {
                pushToDataLayer('configuratorGarageLoad');
                window.localStorage.setItem('garageEmailAddress', emailAddress);
                dispatch(closeModal('loadGarage'));

                dispatch(clearLoadedConfiguration());
                dispatch(resetConfigurator());
                dispatch(resetBike());
                dispatch(resetTagging());

                navigate('/garage');
              }}
              secondaryActionLabel="cancelLabel"
              secondaryActionFunc={() => {
                pushToDataLayer('configuratorGarageCancel');
                dispatch(closeModal('loadGarage'));
              }}
            ></EmailModal>
          </Modal>
        ) : null}
        {openModals.saveToGarage ? (
          <Modal onClose={() => dispatch(closeModal('saveToGarage'))} disableFullScreen>
            <EmailModal
              showAgreeToTerms
              headingLabel="saveConfigurationLabel"
              taglineLabel="saveConfigurationDescription"
              primaryActionLabel="saveLabel"
              primaryActionFunc={(emailAddress, dataConsentDoNotContact, recieveCommunication) => {
                pushToDataLayer('configuratorSaveComplete');
                dispatch(closeModal('saveToGarage'));
                saveToGarage(emailAddress, dataConsentDoNotContact, recieveCommunication, savedConfigurationId => {
                  if (typeof saveModalProps.afterSave === 'function') {
                    saveModalProps.afterSave(savedConfigurationId);
                  }
                });
              }}
              secondaryActionLabel="cancelLabel"
              secondaryActionFunc={() => {
                pushToDataLayer('configuratorSaveCancel');
                dispatch(closeModal('saveToGarage'));
              }}
            />
          </Modal>
        ) : null}
        {openModals.saveToGarageAbandon ? (
          <Modal onClose={() => dispatch(closeModal('saveToGarageAbandon'))} disableFullScreen>
            <EmailModal
              showAgreeToTerms
              isVerticalButtonAlignment
              headingLabel="saveConfigurationLabel"
              taglineLabel="saveConfigurationDescription"
              primaryActionLabel="saveToGarageLabel"
              primaryActionFunc={(emailAddress, dataConsentDoNotContact, recieveCommunication) => {
                pushToDataLayer('configuratorSave');
                dispatch(closeModal('saveToGarageAbandon', false));
                saveToGarage(emailAddress, dataConsentDoNotContact, recieveCommunication, () => {
                  leaveConfigurator();
                });
              }}
              secondaryActionLabel="continueWithoutSavingLabel"
              secondaryActionFunc={() => {
                pushToDataLayer('configuratorUnsaved');
                leaveConfigurator();
              }}
            />
          </Modal>
        ) : null}
        {openModals.share ? (
          <Modal
            onClose={() => [pushToDataLayer('configuratorShareCancel'), dispatch(closeModal('share'))]}
            disableFullScreen
          >
            <ShareModal />
          </Modal>
        ) : null}
        {openModals.expand ? (
          <Modal isFullScreen onClose={() => dispatch(closeModal('expand'))}>
            <BikeImage isFullScreen className="js-fullscreen" />
          </Modal>
        ) : null}
        {openModals.accessories ? (
          <Modal onClose={() => dispatch(closeModal('accessories'))}>
            <AccessoryInfoModal />
          </Modal>
        ) : null}
        {openModals.disclaimer ? (
          <Modal onClose={() => [dispatch(closeModal('disclaimer')), dispatch(setSelectedDisclaimer(null))]}>
            <DisclaimerModal />
          </Modal>
        ) : null}
        {openModals.unsavedConfiguration ? (
          <Modal onClose={() => dispatch(closeModal('unsavedConfiguration'))}>
            <UnsavedConfigurationModal
              onSaveWithoutUser={() => {
                pushToDataLayer('configuratorAbandonSave');
                dispatch(closeModal('unsavedConfiguration'));
                dispatch(openModal('saveToGarage'));
              }}
              onSaving={() => dispatch(closeModal('unsavedConfiguration'))}
              onSaved={() => navigate('/garage')}
              onContinueWithoutSave={() => {
                pushToDataLayer('configuratorAbandonConfirm');

                if (!getGarageEmailAddress()) {
                  dispatch(closeModal('unsavedConfiguration'));
                  dispatch(openModal('loadGarage'));
                  return;
                }

                dispatch(closeModal('unsavedConfiguration'));
                dispatch(resetConfigurator());
                dispatch(resetBike());
                dispatch(resetTagging());

                return navigate('/garage');
              }}
            />
          </Modal>
        ) : null}
        {openModals.unsavedConfigurationAbandon ? (
          <Modal onClose={() => dispatch(closeModal('unsavedConfigurationAbandon'))}>
            <UnsavedConfigurationModal
              isVerticalButtonAlignment
              unsavedConfigurationTitle="unsavedConfigurationTitle"
              unsavedConfigurationSubtitle="unsavedConfigurationSubtitle"
              unsavedConfigurationDescription="unsavedConfigurationDescription"
              saveLabel="saveToGarageLabel"
              onSaveWithoutUser={() => {
                pushToDataLayer('configuratorSaveIntent');
                dispatch(closeModal('unsavedConfigurationAbandon', false));
                dispatch(openModal('saveToGarageAbandon'));
              }}
              onSaving={() => dispatch(closeModal('unsavedConfigurationAbandon', false))}
              onSaved={() => leaveConfigurator()}
              onContinueWithoutSave={() => {
                pushToDataLayer('configuratorUnsaved');
                leaveConfigurator();
                dispatch(closeModal('unsavedConfigurationAbandon', false));
              }}
            />
          </Modal>
        ) : null}
        {openModals.sendToDealer ? (
          <Pane onClose={() => dispatch(closeModal('sendToDealer'))}>
            <SendToDealer />
          </Pane>
        ) : null}
        {saveQuoteModalOpen ? (
          <Modal onClose={() => setSaveQuoteModalOpen(false)} disableFullScreen>
            <SaveQuoteModal
              primaryActionFunc={quoteRef => {
                window.localStorage.setItem('quoteRef', quoteRef);
              }}
              primarySuccessLabel="successLabel"
              secondaryActionFunc={() => {
                setSaveQuoteModalOpen(false);
                pushToDataLayer('saveQuoteCancel');
              }}
            ></SaveQuoteModal>
          </Modal>
        ) : null}
      </Wrap>
      <Tray
        hideOnDesktop={isBikeSummary}
        scriptsLoadedSuccessfully={!scriptLoading}
        isSimulationChecksumExists={isSimulationChecksumExists}
        openShareModal={openShareModal}
      />
      {garageSaving && (
        <>
          <Overlay />
          <Loader />
        </>
      )}
    </>
  );
};

export default Configurator;
